import React, { useEffect, useRef, FC } from 'react';
import { useSelector } from 'react-redux';
import { useActions, ContentFragment, getNavContent } from '@trip-ux/redux-store';
import { isEmpty } from 'lodash';
import { Progress } from '@trip-ux/components';

function redirectToRoot() {
  if (window.location.pathname !== '/') {
    window.history.pushState({}, '', '/');
  }
}

export const NavContentWrapper: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const contentFragment = useSelector((store: { contentFragment: ContentFragment }) => store.contentFragment);
  const locale = useSelector((store: { config?: { locale?: string } }) => store.config?.locale);
  const actions = useActions({ getNavContent });
  // undefined = SSR (no fetch ever initiated); null = fetch initiated but locale was not yet set
  const fetchedLocaleRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!contentFragment.navContentFetched) {
      if (fetchedLocaleRef.current === undefined) {
        // First fetch: record locale so stale-content detection works after it completes
        fetchedLocaleRef.current = locale ?? null;
        actions.getNavContent();
      }
      // else: fetch already in-flight — wait for navContentFetched to become true
      return;
    }
    if (fetchedLocaleRef.current === undefined) {
      // Mounted with content already present (SSR hydration); anchor ref so locale changes can be detected
      fetchedLocaleRef.current = locale ?? null;
    } else if (locale && locale !== fetchedLocaleRef.current) {
      fetchedLocaleRef.current = locale;
      actions.getNavContent();
    }
  }, [contentFragment.navContentFetched, locale, actions]);

  const hasEmptyContent = contentFragment.navContentFetched && isEmpty(contentFragment.contentRoot);

  useEffect(() => {
    if (hasEmptyContent) {
      redirectToRoot();
    }
  }, [hasEmptyContent]);

  if (!contentFragment.navContentFetched) {
    return <Progress />;
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

NavContentWrapper.displayName = 'NavContentWrapper';
export default NavContentWrapper;
